import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const AuthContext = createContext();

// Install a single global response interceptor that catches 401 Unauthorized
// from any request and forces a clean re-auth (clears local token + axios header).
// This prevents the "I need to refresh the page to login" symptom that happens
// when a stale token from a previous session lingers in axios.defaults.
let _interceptorInstalled = false;
const installAuthInterceptor = (onUnauthorized) => {
  if (_interceptorInstalled) return;
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const url = error?.config?.url || "";
      // Ignore 401 from login/register itself — that's a legitimate "bad credentials"
      if (status === 401 && !url.includes("/auth/login") && !url.includes("/auth/register")) {
        try {
          localStorage.removeItem("token");
          delete axios.defaults.headers.common["Authorization"];
          if (typeof onUnauthorized === "function") onUnauthorized();
        } catch (_) { /* ignore */ }
      }
      return Promise.reject(error);
    }
  );
  _interceptorInstalled = true;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [membership, setMembership] = useState(null);

  const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

  const checkMembershipStatus = useCallback(async () => {
    if (!token || !user) return;
    
    // Only check for agents and owners
    if (!['agent', 'owner'].includes(user?.role)) return;
    
    try {
      const response = await axios.get(`${API}/auth/membership-status`);
      setMembership(response.data);
      return response.data;
    } catch (error) {
      console.error("Failed to check membership status", error);
      return null;
    }
  }, [token, user, API]);

  useEffect(() => {
    // Install global 401 interceptor once.
    installAuthInterceptor(() => {
      setToken(null);
      setUser(null);
      setMembership(null);
    });
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      delete axios.defaults.headers.common["Authorization"];
      setLoading(false);
    }
  }, [token]);

  // Periodic membership check (every 5 minutes) for agents/owners
  useEffect(() => {
    if (!user || !['agent', 'owner'].includes(user?.role)) return;
    
    // Initial check
    checkMembershipStatus();
    
    // Set up interval for periodic checks
    const intervalId = setInterval(() => {
      checkMembershipStatus();
    }, 5 * 60 * 1000); // Check every 5 minutes
    
    return () => clearInterval(intervalId);
  }, [user, checkMembershipStatus]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error("Failed to fetch user", error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (tokenData, userData, membershipData = null) => {
    setToken(tokenData);
    setUser(userData);
    setMembership(membershipData);
    localStorage.setItem("token", tokenData);
    axios.defaults.headers.common["Authorization"] = `Bearer ${tokenData}`;
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setMembership(null);
    localStorage.removeItem("token");
    delete axios.defaults.headers.common["Authorization"];
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      logout, 
      membership, 
      checkMembershipStatus 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);